import React, { useState } from "react";
import axios from "axios";
import { getAuthHeaders, useAuth } from "../context/AuthContext";
import { getApiUrl } from "../utils/apiHelper";
import { Box, Typography, TextField, Button, Grid, Card, CardMedia, CardContent, CircularProgress } from "@mui/material";

function PairItUp() {
  const [sku, setSku] = useState("");
  // Results will store the grouped data, e.g., { GDLBBNE: [...], GDLBBPD: [...] }
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { loginType } = useAuth();

  // Function to group products by SKU prefix
  const groupDataBySkuPrefix = (data) => {
    if (!Array.isArray(data)) return {};

    return data.reduce((acc, product) => {
      // Extracts the letter prefix from the SKU (e.g., "GDLBBNE" from "GDLBBNE011")
      const prefixMatch = product.sku.match(/^[A-Za-z]+/);
      const prefix = prefixMatch ? prefixMatch[0] : "Other";

      if (!acc[prefix]) {
        acc[prefix] = [];
      }
      acc[prefix].push(product);
      return acc;
    }, {});
  };

  const handlePairItUp = async () => {
    setLoading(true);
    setResults({});
    setError("");
    try {
      const headers = await getAuthHeaders();
      const body = {
        params: {
          top_k: 10,
          client: "giva",
          filters: {}
        }
      };

      // The actual API call using axios.post
      const res = await axios.post(
        getApiUrl(loginType, `/products/${sku}/pair-it-up`),
        body,
        { headers }
      );

      const groupedData = groupDataBySkuPrefix(res.data);
      setResults(groupedData);

    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to fetch data. Please try again.");
      setResults({});
    }
    setLoading(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Pair It Up</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <TextField
          label="Enter SKU"
          value={sku}
          onChange={e => setSku(e.target.value)}
          sx={{ mr: 2, flexGrow: 1 }}
        />
        <Button variant="contained" onClick={handlePairItUp} disabled={!sku || loading} size="large">
          {loading ? <CircularProgress size={24} /> : "Pair It Up"}
        </Button>
      </Box>

      {error && <Typography color="error">{error}</Typography>}

      {Object.keys(results).length > 0 && (
        <Box sx={{ mt: 4 }}>
          {Object.entries(results).map(([prefix, products]) => (
            <Box key={prefix} sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ textTransform: 'uppercase', borderBottom: '2px solid #eee', pb: 1 }}>
                {prefix}
              </Typography>
              <Grid container spacing={3}>
                {products.map((product) => (
                  <Grid item key={product.sku} xs={12} sm={6} md={4} lg={3}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={product.top_match_image_url}
                        alt={product.sku}
                        sx={{ objectFit: 'contain', p: 1 }} // Use 'contain' to see the whole image
                      />
                      <CardContent>
                        <Typography variant="body1" fontWeight="bold">SKU: {product.sku}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Score: {product.aggregated_score.toFixed(3)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default PairItUp;